import {api, RequestParam} from "@/shared/api";

import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable";
import {RecordingTable} from "@/shared/recording/recordingTable";
import {RecordDetailsDialog, RecordDetailsDialogConfig} from "@/shared/recording/recordDetailsDialog";
import {ValidationsListDialogComponent} from "@/shared/validations/validationsListComponent";
import {AttachmentGallery} from "@/shared/ui/attachmentGallery";
import {ProjectTimeTable} from "./projectTimeTable";
import {breadcrumbsWithoutName, htmlEscape} from "@/shared/utils/string";
import {updateCutInfo} from "@/shared/utils/misc";
import {AttachmentDownloadUrlResponse, ReportProjectTime} from "@/shared/timr";
import {RecordedEntryStatus} from "@/shared/timrTypes";
import {isProjectTimeEditAllowed} from "@/shared/securityHelper";

export class ProjectTimeDetailsDialog extends RecordDetailsDialog<ReportProjectTime> {

	protected projectTimeRevisionTable: ProjectTimeDetailsDialogRevisionTable;

	private readonly validationsList: ValidationsListDialogComponent;
	private readonly attachmentGallery?: AttachmentGallery;

	constructor(config: RecordDetailsDialogConfig<ReportProjectTime>) {
		super(config);

		if (globals.savedValidationsEnabled) {
			this.validationsList = document.getElementById("projectTimeDetailsValidations") as ValidationsListDialogComponent;
		}

		this.attachmentGallery = document.getElementById("projectTimeDetailsAttachments") as AttachmentGallery;
	}

	public show(projectTime: ReportProjectTime) {
		this.loadRevisionTable(projectTime);
		this.setProjectTimeFields(projectTime);
		super.show(projectTime);

		this.toggleEditButton(projectTime, isProjectTimeEditAllowed(projectTime));
		this.updateBorder(projectTime.billable);

		if (globals.savedValidationsEnabled) {
			updateCutInfo(this.dom.find("[data-cut-info]"), projectTime);
			this.validationsList.validationsListItem = {recordId: projectTime.id, validations: projectTime.validations};
		}

		this.loadAttachments(projectTime.id);
	}

	private loadAttachments(projectTimeId: number) {
		if (!this.attachmentGallery) {
			return;
		}

		this.attachmentGallery.reset();

		api("recording/getProjectTimeAttachments.json")
			.get({
				projectTimeId
			})
			.done((data: AttachmentDownloadUrlResponse[]) => {
				this.attachmentGallery.setExistingFiles(data);
			});
	}

	protected setModalTitle(projectTime: ReportProjectTime) {
		const breadCrumbsWithoutName = breadcrumbsWithoutName(projectTime.taskBreadCrumbs || "", projectTime.taskName);
		const title = `<h4 class="no-margin">${htmlEscape(projectTime.taskName)}</h4>
							<h6 class="text-grey no-margin">${breadCrumbsWithoutName}</h6>`;
		this.dialog.setTitle(title);
	}

	/**
	 * Loads the history/revisions of the record
	 * Initializes the table containing this data if not already done
	 * @param {ProjectTime} projectTime
	 * @protected
	 */
	protected loadRevisionTable(projectTime: ReportProjectTime) {
		if (!this.projectTimeRevisionTable) {
			this.projectTimeRevisionTable = new ProjectTimeDetailsDialogRevisionTable({
				allowExpand: false,
				clampNote: false,
				element: this.dom.find("#projectTimeDetailsDialogRevisionTable"),
				orderable: false,
				initialFilter: ProjectTimeDetailsDialogRevisionTable.buildFilterParams(projectTime.id)
			});
			this.projectTimeRevisionTable.reload();
		} else {
			this.projectTimeRevisionTable.filterByProjectTimeId(projectTime.id);
		}
	}

	protected setProjectTimeFields(projectTime: ReportProjectTime) {
		this.setDescriptionField(projectTime);

		const billableValue = projectTime.billable ? `<i class="fal fa-dollar-sign position-left"></i> ${messages["record.details.billable"]}` : messages["record.details.notBillable"];
		this.dom.find("#projectTimeDetailsDialogBillable").html(billableValue);
		this.dom.find(".recordDetailsDialogBreakTime").text(projectTime.breakTime);
	}

	protected updateBorder(billable: boolean) {
		const modalContent = this.dom.parents("div.modal-content");

		modalContent.toggleClass("border-BILLABLE", billable);
		modalContent.toggleClass("border-NOT_BILLABLE", !billable);
		modalContent.removeClass("border-NOT_INITIALIZED");
	}

	protected getStatus(record: ReportProjectTime): RecordedEntryStatus {
		return record.status;
	}
}

export class ProjectTimeDetailsDialogRevisionTable extends BasicDataTable<ReportProjectTime> {

	constructor(public config: BasicDataTableConfig) {
		super("/timr/reports/getProjectTimeRevisions.json", config);
	}

	public static buildFilterParams(projectTimeId: number): RequestParam[] {
		return [{
			name: "id",
			value: projectTimeId
		}];
	}

	public filterByProjectTimeId(projectTimeId: number): JQueryPromise<ReportProjectTime[]> {
		return this.filter(ProjectTimeDetailsDialogRevisionTable.buildFilterParams(projectTimeId));
	}

	protected getColumnSettings(): TypedColumnSettings<ReportProjectTime>[] {
		return [
			{data: "lastModified", orderable: this.config.orderable, render: BasicDataTable.renderDateTimeLocal, visible: true},
			{data: "modifiedByUser", orderable: false, render: BasicDataTable.renderText},
			{data: "taskBreadCrumbs", orderable: false, className: "table-data-task", render: BasicDataTable.renderProjectTimeTaskPath},
			{
				data: "startPlatform",
				orderable: false,
				render: (d, t, r, m) => BasicDataTable.renderPlatformIcon(d, t, r, m, true),
				className: "slim-icon-column"
			},
			{
				data: "start",
				orderable: false,
				render: BasicDataTable.renderDateTimeWithTimeZone,
				className: "pl-10 pr-10 time-column"
			},
			{
				data: "endPlatform",
				orderable: false,
				render: (d, t, r, m) => BasicDataTable.renderPlatformIcon(d, t, r, m, false),
				className: "slim-icon-column"
			},
			{
				data: "breakTime",
				orderable: false,
				render: RecordingTable.renderBreakTime,
				className: "text-right",
				width: BasicDataTable.COLUMN_SIZE_BREAKTIME
			},
			{
				data: "durationNet",
				orderable: false,
				render: RecordingTable.renderDuration,
				className: "text-right",
				width: BasicDataTable.COLUMN_SIZE_DURATION
			},
			{
				data: "description",
				orderable: false,
				className: "table-data-description",
				render: BasicDataTable.renderDescriptionText
			},
			{
				data: "billable",
				orderable: false,
				render: ProjectTimeTable.renderBillable,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			},
			{
				data: "status",
				orderable: false,
				render: RecordingTable.renderStatus,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[5, "desc"]];
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			pageLength: 4,
			deferLoading: 0,
			dom: `rt`
		};
	}
}
